import React, { useState } from "react";
import { func, string, bool, object } from "prop-types";
import { connect, useSelector, useDispatch } from "react-redux";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import ExpandMore from "@material-ui/icons/ExpandMore";
import ChevronRight from "@material-ui/icons/ChevronRight";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import FileCopyOutlined from "@material-ui/icons/FileCopyOutlined";
import Popover from "@material-ui/core/Popover";
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";
import { setDialogStatus } from "redux/app/actions";
import {
  selectImageId,
  selectImageRotating,
  selectImageAnnotationType,
  selectIsBenchmark,
  selectIsBenchmarkProject,
  selectImageInfo,
} from "redux/image/selectors";
import {
  selectIsIndiceShown,
  selectIsLinesShown,
  selectIsIrisCirclesShown,
  selectIsKeyPointShown,
} from "redux/canvas/selectors";
import {
  showPointsIndice,
  showConnectLines,
  showIrisCircles,
  showKeyPoint,
} from "redux/canvas/actions";
import { selectIsOpenStatus } from "redux/app/selectors";
import Dialog from "./Dialog";
import Resizer from "./Resizer";
import ButtonGroupV2 from "./ButtonGroupV2";
import style from "./style.module.scss";
import statusStyle from "../style.module.scss";
import IntlMessage from "../../helpers/IntlMessage";
import { BenchmarkStart } from "icons";
import { getParameterByName } from "helpers";
import { markBenchmark } from "redux/image/actions";
import { selectUserRole } from "redux/permission/selector";
import { injectIntl } from "react-intl";

const ImgAnnotationHeader = ({
  imageId,
  imageInfo,
  isIndiceShown,
  showPointsIndice,
  isLinesShown,
  showConnectLines,
  isIrisCirclesShown,
  showIrisCircles,
  isKeyPointShown,
  showKeyPoint,
  annotationType,
  // imageRotating,
  markBenchmark,
  intl,
}) => {
  // const [dialogOpen, setDialogOpen] = useState(false);
  const dispatch = useDispatch();
  const isOpen = useSelector(selectIsOpenStatus);
  const [copiedKey, setCopiedKey] = useState(null);
  const [typeOfDialog, setTypeOfDialog] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const isViewResult = getParameterByName("isViewResult");
  const isActionHistory = getParameterByName("isActionHistory");

  const roleCode = useSelector(selectUserRole);
  const isBenchmark = useSelector(selectIsBenchmark);
  const isBenchmarkProject = useSelector(selectIsBenchmarkProject);
  const enableBenchmark =
    roleCode === "admin" || roleCode === "project_manager";

  function handleOpenDialog(e) {
    let typeOfDialog = e.currentTarget.getAttribute("data-content");
    setTypeOfDialog(typeOfDialog);
    // setDialogOpen(true);
    dispatch(setDialogStatus(true));
  }

  function handleShowIndice(event) {
    showPointsIndice(event.target.checked);
  }

  function handleShowLines(event) {
    showConnectLines(event.target.checked);
  }

  function handleShowKeyPoint(event) {
    showKeyPoint(event.target.checked);
  }

  function handleClickShow(event) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleShowIrisCircles(event) {
    showIrisCircles(event.target.checked);
  }

  const handleMarkBenchmark = () => {
    markBenchmark && markBenchmark({ isBenchmark: !isBenchmark });
  };

  const { name, size } = imageInfo.metadata ?? {};
  const imageUrl = imageInfo.imageUrl;
  const status = imageInfo.status;
  const open = Boolean(anchorEl);

  const formatSize = (bytes) => {
    if (!bytes) return "—";
    const mb = bytes / (1024 * 1024);
    if (mb < 0.7) {
      return (bytes / 1024).toFixed(2) + " KB";
    }
    return mb.toFixed(2) + " MB";
  };

  const onCopyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <>
      <Dialog open={isOpen} typeOfDialog={typeOfDialog} />
      <div className={style.mediaTopBar}>
        <div className={style.imageInfoArea}>
          <div className={style.infoIconWrapper}>
            <InfoOutlined className={style.infoIcon} fontSize="small" />
            <div className={style.infoPopup}>
              <div className={style.infoRow}>
                <span className={style.infoLabel}>ID</span>
                <button
                  className={style.infoCopyBtn}
                  onClick={() => onCopyText(imageId, "id")}
                  title={imageId}
                >
                  <FileCopyOutlined className={style.infoCopyIcon} />
                  <span className={style.infoCopyText}>{imageId || "—"}</span>
                  {copiedKey === "id" && (
                    <span className={style.copiedTag}>Copied!</span>
                  )}
                </button>
              </div>
              <div className={style.infoRow}>
                <span className={style.infoLabel}>Status</span>
                <span
                  className={`${style.statusBadge} ${
                    statusStyle[`status_${status}`] || ""
                  }`}
                >
                  {status && <IntlMessage id={`common.${status}`} />}
                </span>
              </div>
              <div className={style.infoRow}>
                <span className={style.infoLabel}>Name</span>
                <button
                  className={style.infoCopyBtn}
                  onClick={() => onCopyText(name, "name")}
                  title={name}
                >
                  <FileCopyOutlined className={style.infoCopyIcon} />
                  <span className={style.infoCopyText}>{name || "—"}</span>
                  {copiedKey === "name" && (
                    <span className={style.copiedTag}>Copied!</span>
                  )}
                </button>
              </div>
              <div className={style.infoRow}>
                <span className={style.infoLabel}>URL</span>
                <button
                  className={style.infoCopyBtn}
                  onClick={() => onCopyText(imageUrl, "url")}
                  title="Copy image URL"
                >
                  <FileCopyOutlined className={style.infoCopyIcon} />
                  <span className={style.infoCopyText}>Copy link</span>
                  {copiedKey === "url" && (
                    <span className={style.copiedTag}>Copied!</span>
                  )}
                </button>
              </div>
              <div className={style.infoRow}>
                <span className={style.infoLabel}>Size</span>
                <span className={style.infoValue}>{formatSize(size)}</span>
              </div>
            </div>
          </div>
        </div>
        {imageId && <ButtonGroupV2 handleOpenDialog={handleOpenDialog} />}
        <div className={style.imageSizeContainer}>
          {annotationType === "image-facial" && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  margin: "0 15px",
                  cursor: "pointer",
                }}
                onClick={handleClickShow}
              >
                <div>{open ? <ChevronRight /> : <ExpandMore />}</div>
                <div>Show</div>
              </div>
              <Popover
                id="show-popover"
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
              >
                <div>
                  &nbsp; &nbsp;
                  <FormControlLabel
                    value="Indice"
                    control={
                      <Checkbox
                        onChange={handleShowIndice}
                        checked={isIndiceShown}
                        color="primary"
                        size="small"
                      />
                    }
                    label={<span className={style.labelValue}>Indice</span>}
                    labelPlacement="end"
                    disabled={!imageId}
                  />
                </div>
                <div>
                  &nbsp; &nbsp;
                  <FormControlLabel
                    value="ConnectLines"
                    control={
                      <Checkbox
                        onChange={handleShowLines}
                        checked={isLinesShown}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <span className={style.labelValue}>
                        <IntlMessage id="img-anno-header.connect-lines" />
                      </span>
                    }
                    labelPlacement="end"
                    disabled={!imageId}
                  />
                </div>
                <div>
                  &nbsp; &nbsp;
                  <FormControlLabel
                    value="IrisCirleDisplay"
                    control={
                      <Checkbox
                        onChange={handleShowIrisCircles}
                        checked={isIrisCirclesShown}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <span className={style.labelValue}>
                        <IntlMessage id="img-anno-header.iris-circles" />
                      </span>
                    }
                    labelPlacement="end"
                    disabled={!imageId}
                  />
                </div>
                <div>
                  &nbsp; &nbsp;
                  <FormControlLabel
                    value="Key Point"
                    control={
                      <Checkbox
                        onChange={handleShowKeyPoint}
                        checked={isKeyPointShown}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <span className={style.labelValue}>
                        <IntlMessage id="img-anno-header.key-point" />
                      </span>
                    }
                    labelPlacement="end"
                    disabled={!imageId}
                  />
                </div>
              </Popover>
            </>
          )}
          {isBenchmarkProject &&
            !isViewResult &&
            !isActionHistory &&
            enableBenchmark && (
              <div>
                <Tooltip
                  placement="bottom-start"
                  id={"benchmark"}
                  title={intl.formatMessage({
                    id: isBenchmark ? "Remove benchmark" : "Add benchmark",
                  })}
                  arrow
                  classes={{
                    tooltip: style.tooltip,
                    arrow: style.tooltipArrow,
                  }}
                >
                  <span
                    className={style.benchmarkIcon}
                    onClick={handleMarkBenchmark}
                  >
                    <BenchmarkStart isBenchmark={isBenchmark} />
                  </span>
                </Tooltip>
              </div>
            )}
          {/* Button rotate */}
          {/* {!imageRotating &&
            size &&
            size < 15 * 1024 * 1024 &&
            !name.includes(".tif") && <Rotate />} */}
          <Resizer />
        </div>
      </div>
    </>
  );
};

ImgAnnotationHeader.propTypes = {
  imageInfo: object,
  isIndiceShown: bool,
  showPointsIndice: func,
  imageId: string,
  isLinesShown: bool,
  showConnectLines: func,
  isIrisCirclesShown: bool,
  showIrisCircles: func,
  isKeyPointShown: bool,
  showKeyPoint: func,
  annotationType: string,
  imageRotating: bool,
  markBenchmark: func,
  intl: object,
};

const mapStateToProps = (state) => ({
  imageInfo: selectImageInfo(state),
  imageId: selectImageId(state),
  isIndiceShown: selectIsIndiceShown(state),
  isLinesShown: selectIsLinesShown(state),
  isIrisCirclesShown: selectIsIrisCirclesShown(state),
  isKeyPointShown: selectIsKeyPointShown(state),
  annotationType: selectImageAnnotationType(state),
  imageRotating: selectImageRotating(state),
});

export default injectIntl(
  connect(mapStateToProps, {
    showPointsIndice,
    showConnectLines,
    showIrisCircles,
    showKeyPoint,
    markBenchmark,
  })(ImgAnnotationHeader)
);
